import React from "react";
import { useAuth } from "react-oidc-context";
import {
  Box,
  Button,
  Container,
  ContentLayout,
  Grid,
  Header,
  Link,
  SpaceBetween,
} from "@amzn/awsui-components-react-v3/polaris";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../components/page-layout";
import { PagePaths } from "../lib/constants";
import { UserProfileHeader } from "../components/user-profile-header";

interface HomePageProps {
  isManager: boolean;
  otpEnabled: boolean;
}

export const Home = ({ isManager, otpEnabled }: HomePageProps) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const loggedInUserAlias =
    (auth.user?.profile?.preferred_username as string) ?? "";
  const loggedInUserDisplayName = (auth.user?.profile?.name as string) ?? "";
  const loggedInUserKILMId = (auth.user?.profile?.sub as string) ?? "";
  const welcome = loggedInUserDisplayName
    ? `Welcome, ${loggedInUserDisplayName}!`
    : "Welcome!";
  return (
    <PageLayout auth={auth}>
      <ContentLayout
        headerVariant="high-contrast"
        header={<Header variant="h1">{welcome}</Header>}
        defaultPadding
      >
        <SpaceBetween size="s">
          <Container>
            <SpaceBetween size="xxs">
              <Header variant="h2">
                Frontier Auth - Amazon Leo's Internal Identity Provider
              </Header>
              <Box variant="p">
                Frontier Auth is the system responsible for answering the
                question of authentication: Is the user/automation/system that
                claims to be, actually the user/automation/system they claim to
                be? Frontier Auth is a part of the Kuiper Identity and Lifecycle
                Management (KILM) system.
              </Box>
              <Link
                href="https://w.amazon.com/bin/view/KuiperEnterpriseTechnology/KAAP/KIAM/KILM#HIdentityProvider28IdP29"
                external={true}
                variant="primary"
                ariaLabel="Learn more about Frontier Auth"
              >
                Learn more
              </Link>
            </SpaceBetween>
          </Container>
          <Container>
            <Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
              <UserProfileHeader
                user={{
                  alias: loggedInUserAlias,
                  displayName: loggedInUserDisplayName,
                  id: loggedInUserKILMId,
                }}
                showActionsDropdown={false}
              />
              <Box float="right" padding={{ top: "m" }}>
                <SpaceBetween direction="vertical" size="xs">
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={() => navigate(PagePaths.MY_PROFILE)}
                  >
                    Go to profile
                  </Button>
                  {isManager && (
                    <Button
                      fullWidth
                      onClick={() => navigate(PagePaths.MANAGE_DIRECTS)}
                    >
                      Manage team
                    </Button>
                  )}
                </SpaceBetween>
              </Box>
            </Grid>
          </Container>
          <Container>
            <SpaceBetween size="xxs">
              <Header variant="h3">What do you need to do today?</Header>
              <Box variant="p">
                To view and manage your profile and credentials,{" "}
                <Link
                  href={PagePaths.MY_PROFILE}
                  onFollow={(e) => {
                    e.preventDefault();
                    navigate(PagePaths.MY_PROFILE);
                  }}
                >
                  click here
                </Link>
                .
              </Box>
              {isManager && (
                <Box variant="p">
                  To view and manage your direct reports,{" "}
                  <Link
                    href={PagePaths.MANAGE_DIRECTS}
                    onFollow={(e) => {
                      e.preventDefault();
                      navigate(PagePaths.MANAGE_DIRECTS);
                    }}
                  >
                    click here
                  </Link>
                  .
                </Box>
              )}
              <Box variant="p">
                If you need to register a new security key,{" "}
                <Link
                  href={PagePaths.REGISTER_MFA}
                  onFollow={(e) => {
                    e.preventDefault();
                    navigate(PagePaths.REGISTER_MFA);
                  }}
                >
                  click here
                </Link>
                .
              </Box>
              {otpEnabled && [
                <Box key="set-otp" variant="p">
                  If you need to set or change your OTP PIN,{" "}
                  <Link
                    href={PagePaths.SET_OTP_PIN}
                    onFollow={(e) => {
                      e.preventDefault();
                      navigate(PagePaths.SET_OTP_PIN);
                    }}
                  >
                    click here
                  </Link>
                  .
                </Box>,
              ]}
              <Box variant="p">
                If you need to set or change your badge PIN,{" "}
                <Link
                  href={PagePaths.SET_BADGE_PIN}
                  onFollow={(e) => {
                    e.preventDefault();
                    navigate(PagePaths.SET_BADGE_PIN);
                  }}
                >
                  click here
                </Link>
                .
              </Box>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </ContentLayout>
    </PageLayout>
  );
};
